import dayjs from 'dayjs'
import Vue from 'vue'
import Vuex from 'vuex'
import createdPersistedState from 'vuex-persistedstate'
import GraphqlAPI from '../utils/GraphqlAPI'
import { graphqlAPIRequester } from '../utils/graphql/GraphqlApiRequester'
import { continueBonusProcessorMutation } from '../utils/graphql/graphqlSchema/Mutations'

Vue.use(Vuex)

const recentCampaignQuery = () => `
  query getRecentCampaign {
    getRecentCampaign {
      campaign {
        id
        name
        start
        end
        is_continuity
        questionnaire_start
        questionnaire_end
        relational_campaign_id
        site_open
        site_close
        is_team
        is_questionnaire
        is_prize
        is_prize_select
        is_app_challenge
        is_learning
        is_continue_bonus
        is_course_select_validation
        is_step_count_google_fit
        max_course_select
        course_deletable_end
        app_deadline_date
        prize_mail_send_date
        is_board
        required_advanced_course_count
        required_total_course_count
        participants
        how_to_join
      }
      option_flags {
        is_walking_travel
      }
    }
  }
`
const fetchRecentCampaignInfo = async () => {
  const { data: { getRecentCampaign: data } } = await GraphqlAPI(recentCampaignQuery())
  return data
}

const campaignQuery = (id) => `
  query getCampaign {
    getCampaign(id: "${id}") {
      campaign {
        id
        name
        start
        end
        is_continuity
        questionnaire_start
        questionnaire_end
        relational_campaign_id
        site_open
        site_close
        is_team
        is_questionnaire
        is_prize
        is_prize_select
        is_app_challenge
        is_learning
        is_continue_bonus
        is_course_select_validation
        is_step_count_google_fit
        max_course_select
        course_deletable_end
        app_deadline_date
        prize_mail_send_date
        is_board
        required_advanced_course_count
        required_total_course_count
        participants
        how_to_join
      }
      option_flags {
        is_walking_travel
      }
    }
  }
`
const fetchCampaignInfo = async (campaignId) => {
  const { data: { getCampaign: data } } = await GraphqlAPI(campaignQuery(campaignId))
  return data
}

const store = new Vuex.Store({
  strict: true,
  plugins: [createdPersistedState({
    key: 'tmnfkenpo',
    paths: [
      'campaign',
      'campaignOption',
      'relationalCampaign',
      'relationalCampaignOption',
      'emailVerified',
      'registeredCourse',
      'appEntryStatus'
    ],
    storage: window.sessionStorage
  })],
  state: {
    isAuthenticated: false,
    user: {},
    campaign: {},
    campaignOption: {},
    relationalCampaign: {},
    relationalCampaignOption: {},
    emailVerified: false,
    registeredCourse: false,
    appEntryStatus: null,
    continueBonusHistorys: [],
    continueBonus: {}
  },
  getters: {
    getCampaign (state) {
      return state.campaign
    },
    getCampaignOption (state) {
      return state.campaignOption
    },
    getRelationalCampaign (state) {
      return state.relationalCampaign
    },
    getRelationalCampaignOption (state) {
      return state.relationalCampaignOption
    },
    getEmailVerified (state) {
      return state.emailVerified
    },
    getRegisteredCourse (state) {
      return state.registeredCourse
    },
    getAppEntryStatus (state) {
      return state.appEntryStatus
    },
    getContinueBonusHistorys (state) {
      return state.continueBonusHistorys
    },
    getContinueBonus (state) {
      return state.continueBonus
    }
  },
  mutations: {
    isAuthenticated (state, isAuthenticated) {
      state.isAuthenticated = isAuthenticated
    },
    setUser (state, user) {
      state.user = user
    },
    setEmailVerified (state, emailVerified) {
      state.emailVerified = emailVerified
    },
    setCampaign (state, campaign) {
      state.campaign = campaign
    },
    setCampaignOption (state, campaignOption) {
      state.campaignOption = campaignOption
    },
    setRelationalCampaign (state, relationalCampaign) {
      state.relationalCampaign = relationalCampaign
    },
    setRelationalCampaignOption (state, relationalCampaignOption) {
      state.relationalCampaignOption = relationalCampaignOption
    },
    setRegisteredCourse (state, registeredCourse) {
      state.registeredCourse = registeredCourse
    },
    setAppEntryStatus (state, appEntryStatus) {
      state.appEntryStatus = appEntryStatus
    },
    setContinueBonusHistorys (state, continueBonusHistorys) {
      state.continueBonusHistorys = continueBonusHistorys
    },
    setContinueBonus (state, continueBonus) {
      state.continueBonus = continueBonus
    }
  },
  actions: {
    setIsAuthenticated ({ commit }, isAuthenticated) {
      commit('isAuthenticated', isAuthenticated)
    },
    setUser ({ commit }, user) {
      commit('setUser', user)
    },
    setEmailVerified ({ commit }, emailVerified) {
      commit('setEmailVerified', emailVerified)
    },
    setRegisteredCourse ({ commit }, registeredCourse) {
      commit('setRegisteredCourse', registeredCourse)
    },
    setAppEntryStatus ({ commit }, appEntryStatus) {
      commit('setAppEntryStatus', appEntryStatus)
    },
    async initCampaign ({ commit }) {
      try {
        const fetchRecentCampaignDataResult = await fetchRecentCampaignInfo()
        commit('setCampaign', fetchRecentCampaignDataResult.campaign)
        commit('setCampaignOption', fetchRecentCampaignDataResult.option_flags ? fetchRecentCampaignDataResult.option_flags : {})
        const relationalCampaignId = fetchRecentCampaignDataResult.campaign.relational_campaign_id
        if (relationalCampaignId) {
          const fetchRelationalCampaignDataResult = await fetchCampaignInfo(relationalCampaignId)
          commit('setRelationalCampaign', fetchRelationalCampaignDataResult.campaign)
          commit('setRelationalCampaignOption', fetchRelationalCampaignDataResult.option_flags ? fetchRelationalCampaignDataResult.option_flags : {})
        }
      } catch (e) {
        console.log(e)
      }
    },
    async setContinueBonusProcessorOutput ({ commit }, { campaignId, type }) {
      const continueBonusApiResponse = await graphqlAPIRequester({ input: { campaignId, type }, func: continueBonusProcessorMutation })
      const continueBonusProcessor = continueBonusApiResponse.continueBonusProcessor
      const ContinueBonusHistorys = continueBonusProcessor.historys.map(history => {
        history.start = dayjs(history.start).format('MM/DD')
        history.end = dayjs(history.end).format('MM/DD')
        return history
      })
      const continueBonus = {
        incentive_requirement_week: continueBonusProcessor.incentive_requirement_week,
        is_continue_bonus_complete: continueBonusProcessor.is_continue_bonus_complete,
        continue_bonus_point: continueBonusProcessor.continue_bonus_point
      }
      commit('setContinueBonusHistorys', ContinueBonusHistorys)
      commit('setContinueBonus', continueBonus)
      if (type === 'write') {
        return continueBonusProcessor
      }
    },
    changeCampaign ({ commit }) {
      const _relationalCampaign = this.state.relationalCampaign
      const _relationalCampaignOption = this.state.relationalCampaignOption
      const _campaign = this.state.campaign
      const _campaignOption = this.state.campaignOption
      commit('setCampaign', _relationalCampaign)
      commit('setCampaignOption', _relationalCampaignOption)
      commit('setRelationalCampaign', _campaign)
      commit('setRelationalCampaignOption', _campaignOption)
      commit('setAppEntryStatus', null)
    },
    signOut ({ commit }) {
      commit('isAuthenticated', false)
      commit('setUser', {})
      commit('setEmailVerified', false)
      commit('setRegisteredCourse', false)
      commit('setCampaign', {})
      commit('setCampaignOption', {})
      commit('setRelationalCampaign', {})
      commit('setRelationalCampaignOption', {})
      commit('setAppEntryStatus', null)
    }
  }
})

Vue.use(store)

export default store
